// CSV Export Utilities for Access70 LMS

export const exportAttendanceToCSV = (attendanceData, filename = 'attendance_report') => {
  try {
    if (!attendanceData || !Array.isArray(attendanceData) || attendanceData.length === 0) {
      alert('No attendance data available to export');
      return;
    }

    // Define CSV headers
    const headers = ['Learner Name', 'Email', 'Module', 'Date', 'Status', 'Marked By', 'Time'];
    
    // Convert data to CSV format
    const csvData = attendanceData.map(record => [
      (record && record.learnerName) || 'N/A',
      (record && record.email) || 'N/A',
      (record && record.module) || 'N/A',
      (record && record.date) ? new Date(record.date).toLocaleDateString() : 'N/A',
      (record && record.status) || 'N/A',
      (record && record.markedBy) || 'N/A',
      (record && record.timestamp) ? new Date(record.timestamp).toLocaleString() : 'N/A'
    ]);

    // Combine headers and data
    const csvContent = [headers, ...csvData];
    
    // Convert to CSV string
    const csvString = csvContent.map(row => 
      row.map(cell => `"${cell}"`).join(',')
    ).join('\n');

    // Create and download the file
    downloadCSV(csvString, `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
    
  } catch (error) {
    console.error('Error generating CSV:', error);
    alert('Error generating CSV report. Please try again.');
  }
};

export const exportUsersToCSV = (users, filename = 'users_report') => {
  try {
    if (!users || !Array.isArray(users) || users.length === 0) {
      alert('No user data available to export');
      return;
    }

    // Define CSV headers
    const headers = ['Name', 'Email', 'Role', 'Status', 'Enrolled Date', 'User ID'];
    
    // Convert data to CSV format
    const csvData = users.map(user => [
      (user && user.displayName) || 'N/A',
      (user && user.email) || 'N/A',
      (user && user.role) || 'N/A',
      (user && user.status) || 'N/A',
      (user && user.enrolledDate) ? new Date(user.enrolledDate).toLocaleDateString() : 'N/A',
      (user && user.uid) || 'N/A'
    ]);

    // Combine headers and data
    const csvContent = [headers, ...csvData];
    
    // Convert to CSV string
    const csvString = csvContent.map(row => 
      row.map(cell => `"${cell}"`).join(',')
    ).join('\n');

    // Create and download the file
    downloadCSV(csvString, `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
    
  } catch (error) {
    console.error('Error generating CSV:', error);
    alert('Error generating CSV report. Please try again.');
  }
};

export const exportIssuesTrackingToCSV = (issues, filename = 'issues_report') => {
  try {
    if (!issues || !Array.isArray(issues) || issues.length === 0) {
      alert('No issue data available to export');
      return;
    }

    // Define CSV headers
    const headers = ['ID', 'Title', 'Description', 'Priority', 'Status', 'Reporter', 'Assignee', 'Created Date', 'Updated Date'];
    
    // Convert data to CSV format
    const csvData = issues.map(issue => [
      (issue && issue.id) || 'N/A',
      (issue && issue.title) || 'N/A',
      (issue && issue.description) || 'N/A',
      (issue && issue.priority) || 'N/A',
      (issue && issue.status) || 'N/A',
      (issue && issue.reportedByName) || 'N/A',
      (issue && issue.assignedTo) || 'N/A',
      (issue && issue.createdAt) ? new Date(issue.createdAt).toLocaleDateString() : 'N/A',
      (issue && issue.updatedAt) ? new Date(issue.updatedAt).toLocaleDateString() : 'N/A'
    ]);

    // Combine headers and data
    const csvContent = [headers, ...csvData];
    
    // Convert to CSV string
    const csvString = csvContent.map(row => 
      row.map(cell => `"${cell}"`).join(',')
    ).join('\n');

    // Create and download the file
    downloadCSV(csvString, `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
    
  } catch (error) {
    console.error('Error generating CSV:', error);
    alert('Error generating CSV report. Please try again.');
  }
};

export const exportAnalyticsToCSV = (analyticsData, filename = 'analytics_report') => {
  try {
    if (!analyticsData || typeof analyticsData !== 'object') {
      alert('No analytics data available to export');
      return;
    }

    let csvContent = [];
    
    // Add summary statistics
    csvContent.push(['Summary Statistics', '', '']);
    csvContent.push(['Metric', 'Value', 'Date']);
    
    if (analyticsData.totalUsers) {
      csvContent.push(['Total Users', analyticsData.totalUsers, new Date().toLocaleDateString()]);
    }
    if (analyticsData.activeUsers) {
      csvContent.push(['Active Users', analyticsData.activeUsers, new Date().toLocaleDateString()]);
    }
    if (analyticsData.completionRate) {
      csvContent.push(['Completion Rate (%)', analyticsData.completionRate, new Date().toLocaleDateString()]);
    }
    if (analyticsData.avgProgress) {
      csvContent.push(['Average Progress (%)', analyticsData.avgProgress, new Date().toLocaleDateString()]);
    }
    
    // Add detailed data if available
    if (analyticsData.detailedData && Array.isArray(analyticsData.detailedData) && analyticsData.detailedData.length > 0) {
      csvContent.push(['', '', '']); // Empty row for separation
      csvContent.push(['Detailed Analytics', '', '']);
      csvContent.push(['Metric', 'Value', 'Category', 'Date']);
      
      analyticsData.detailedData.forEach(item => {
        csvContent.push([
          (item && item.metric) || 'N/A',
          (item && item.value) || 'N/A',
          (item && item.category) || 'N/A',
          (item && item.date) ? new Date(item.date).toLocaleDateString() : 'N/A'
        ]);
      });
    }
    
    // Convert to CSV string
    const csvString = csvContent.map(row => 
      row.map(cell => `"${cell}"`).join(',')
    ).join('\n');

    // Create and download the file
    downloadCSV(csvString, `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
    
  } catch (error) {
    console.error('Error generating CSV:', error);
    alert('Error generating CSV report. Please try again.');
  }
};

export const exportLearnerProgressToCSV = (learnerProgress, filename = 'learner_progress_report') => {
  try {
    if (!learnerProgress || !Array.isArray(learnerProgress) || learnerProgress.length === 0) {
      alert('No learner progress data available to export');
      return;
    }

    // Define CSV headers
    const headers = ['Learner Name', 'Email', 'Module', 'Progress (%)', 'Status', 'Start Date', 'Completion Date', 'Time Spent (Hours)'];
    
    // Convert data to CSV format
    const csvData = learnerProgress.map(record => [
      (record && record.learnerName) || 'N/A',
      (record && record.email) || 'N/A',
      (record && record.module) || 'N/A',
      (record && record.progress) || '0',
      (record && record.status) || 'N/A',
      (record && record.startDate) ? new Date(record.startDate).toLocaleDateString() : 'N/A',
      (record && record.completionDate) ? new Date(record.completionDate).toLocaleDateString() : 'N/A',
      (record && record.timeSpent) || '0'
    ]);

    // Combine headers and data
    const csvContent = [headers, ...csvData];
    
    // Convert to CSV string
    const csvString = csvContent.map(row => 
      row.map(cell => `"${cell}"`).join(',')
    ).join('\n');

    // Create and download the file
    downloadCSV(csvString, `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
    
  } catch (error) {
    console.error('Error generating CSV:', error);
    alert('Error generating CSV report. Please try again.');
  }
};

// Helper function to download CSV file
const downloadCSV = (csvString, filename) => {
  try {
    // Create a Blob with the CSV data
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    
    // Create a temporary URL for the blob
    const url = URL.createObjectURL(blob);
    
    // Create a temporary anchor element to trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    
    // Append to body, click, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up the URL object
    URL.revokeObjectURL(url);
    
  } catch (error) {
    console.error('Error downloading CSV:', error);
    alert('Error downloading CSV file. Please try again.');
  }
};

// Utility function to escape CSV special characters
const escapeCSVField = (field) => {
  if (field === null || field === undefined) return '';
  
  const stringField = String(field);
  
  // If the field contains comma, newline, or quotes, wrap it in quotes
  if (stringField.includes(',') || stringField.includes('\n') || stringField.includes('"')) {
    // Escape existing quotes by doubling them
    return `"${stringField.replace(/"/g, '""')}"`;
  }
  
  return stringField;
};

// Export all functions as named exports
export {
  downloadCSV,
  escapeCSVField
};